import { Stack } from '@fluentui/react';
import { CSSProperties, useMemo } from 'react';
import shallow from 'zustand/shallow';
import { useStore } from '../../../context/store';
import { CellRenderInfo } from './RangePicker';
import { getSameComparitor, getFormat, format } from '../helpers';
import { ArrowNextFilled, ArrowPreviousFilled } from '@fluentui/react-icons';
import { DateResolution } from '../../../api/generated/data-contracts';

export const CalendarCell = ({ info, date }: { info: CellRenderInfo; date: Date }) => {
    const { timelineMeta, resolution } = useStore(
        store => ({
            timelineMeta: store.timelineMeta,
            resolution: store.timeline.resolution,
        }),
        shallow,
    );

    const isSame = useMemo(() => getSameComparitor(resolution), [resolution]);

    const { start, end } = useMemo(() => {
        if (timelineMeta && resolution != null) {
            return {
                start: isSame(timelineMeta.start, date),
                end: isSame(timelineMeta.end, date),
            };
        }
        return {
            start: false,
            end: false,
        };
    }, [date, isSame, resolution, timelineMeta]);

    const todayBorder = useMemo<CSSProperties | undefined>(() => {
		// Day and Week already has a border around the cell for today.
		// Give the other resolutions the same border for the cell that includes today.
        if (resolution !== DateResolution.Day && resolution !== DateResolution.Week && isSame(date, info.today)) {
            return { border: '1px solid #1677ff', borderRadius: 4 };
        }
    }, [date, info.today, isSame, resolution]);

    const title = useMemo(() => {
        if (timelineMeta && (start || end)) {
            // TODO - localize
            let infoText: string;
            if (start && end) {
                infoText = 'starts and ends';
            } else if (start) {
                infoText = 'starts';
            } else if (end) {
                infoText = 'ends';
            }
            return `${timelineMeta.name} ${infoText} - ${format(date, getFormat(info, resolution))}`;
        }
    }, [date, end, info, resolution, start, timelineMeta]);

    return (
        <div className="ant-picker-cell-inner" title={title} style={todayBorder}>
            <Stack horizontal horizontalAlign="center" verticalAlign="center">
                {start && <StartMarker />} {info.originNode.props.children} {end && <EndMarker />}
            </Stack>
        </div>
    );
};

const StartMarker = () => {
    return (
        <div style={{ height: 15, width: 15, display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: 15 }}>
            <ArrowPreviousFilled />
        </div>
    );
};

const EndMarker = () => {
    return (
        <div style={{ height: 15, width: 15, display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: 15 }}>
            <ArrowNextFilled />
        </div>
    );
};
